<template>
  <FullPageLoader v-bind="loaderProps" v-if="isFakeLoading">
    <div class="loader-text">
      <span v-if="desktop">Details confirmed. Preparing your booking page...</span>
      <span v-else>Details confirmed.<br />Preparing your booking page...</span>
    </div>
    <span>Did you know? Plans like yours are...</span>
  </FullPageLoader>

  <div class="launch-preview" v-else>
    <Card class="ready-to-share">
      <h1 class="ready-to-share__title">Let's launch your trip and get some bookings!</h1>
      <span>Launch your itinerary to start collecting sign-ups. You can always message us to edit details after.</span>
    </Card>
    <BackButton :handleClick="goBack" :text="'Back to preview booking page'" :href="backUrl" />
    <TripLaunchCard v-if="!desktop" />
    <Layout>
      <template #left>
        <TripTimeline v-if="store.data" />
        <PublishTripFaq />
        <TripEditContactUs :onClick="goToMessages" />
      </template>
      <template #right>
        <TripLaunchCard v-if="desktop" />
        <TripLaunchGuideline :progress="4" open />
      </template>
    </Layout>
    <ItineraryFeedback />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import BackButton from '../Itinerary/Components/BackButton.vue'
import {
  Layout,
  TripTimeline,
  TripLaunchCard,
  TripLaunchGuideline,
  PublishTripFaq,
  TripEditContactUs,
} from '@/modules/SelfServePublish'
import { useItineraryStore } from '@/stores/itinerary.js'
import Card from '@/components/Theme2/Card.vue'
import { useResponsive, useShared } from '@/helpers/vueHelpers.js'
import { useSelfServePublishStages, SelfServePublishStages } from '@/helpers/hooks'
import MagicHatSvg from '../../components/Loader/icons/MagicHatSvg.vue'
import { FullPageLoader, type SingleLineProps } from '../../components/Loader'
import { usePublishSetupStore } from '../../stores/publishSetup'
import ItineraryFeedback from '@/modules/Itinerary/Components/ItineraryFeedback/index.vue'

const loaderProps: SingleLineProps = {
  mode: 'single',
  IconComponent: MagicHatSvg,
  stages: [
    {
      sentence: '✈️ Built from real trips that have already run',
      checked: false,
    },
    {
      sentence: '🗺️ Checked with experienced local guides',
      checked: false,
    },
    {
      sentence: '🤝 Designed to work smoothly for real groups',
      checked: false,
    },
    {
      sentence: '⭐️ Receive a 4.5-star rating from travelers',
      checked: false,
    },
  ],
}

const { desktop } = useResponsive()
const router = useRouter()
const store = useItineraryStore()
const { componentInfo } = useShared()
const isFakeLoading = ref(true)
const backUrl = router.currentRoute.value.path.replace('/launch-preview', '/preview')
const publishSetupStore = usePublishSetupStore()

const contactUsUrl = computed(() => {
  return store.data?.itineraryRequestId
    ? `/travelers/messages?itineraryRequestId=${store.data.itineraryRequestId}`
    : '/'
})
store.initialize()

useSelfServePublishStages({
  stage: SelfServePublishStages.PENDING_LAUNCH,
  isProduct: true,
  componentInfo,
})

onMounted(() => {
  window.scrollTo({ top: 0, behavior: 'smooth' })

  const randomizedDelay = Math.random() * 1000 + 500
  setTimeout(() => {
    isFakeLoading.value = false
  }, randomizedDelay)
})

const goBack = () => {
  router.push(backUrl)
}

const goToMessages = () => {
  publishSetupStore.setIsFeedbackOpen(true)
}
</script>

<style lang="scss" scoped>
.launch-preview {
  @include flex-column($spacing-6);
  padding-block: $spacing-8;
}

.ready-to-share {
  @include flex-column($spacing-3);
  background: var(--Linear-Blue, linear-gradient(222deg, #1c355e 14.34%, #538c97 85.42%));
  color: $white;
  text-align: center;

  .ready-to-share__title {
    font-weight: $font-semibold;
    margin-block: 0;

    @include mobile {
      font-size: $text-2xl;
    }
  }
}

.loader-text {
  margin-top: $spacing-4;
  font-weight: $font-medium;
}
</style>
